import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import imageCompression from "browser-image-compression";

export default function ImageUploadSection({
  imageFiles,
  setImageFiles,
  previews,
  setPreviews,
}) {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const latestPreviewsRef = useRef(previews);

  const [isProcessing, setIsProcessing] =
    useState(false);

  /**
   * ============================================================
   * KEEP LATEST PREVIEW REFERENCES
   * ============================================================
   */

  useEffect(() => {
    latestPreviewsRef.current = previews;
  }, [previews]);

  /**
   * Clean up generated blob URLs only when this component
   * actually unmounts.
   */

  useEffect(() => {
    return () => {
      latestPreviewsRef.current.forEach(
        (url) => {
          if (url?.startsWith?.("blob:")) {
            URL.revokeObjectURL(url);
          }
        }
      );
    };
  }, []);

  /**
   * ============================================================
   * PROCESS + APPEND IMAGES
   * ============================================================
   *
   * Both native-camera photos and selected files come through
   * this function so ordering stays consistent.
   * ============================================================
   */

  const processAndAppendImages = async (
    incomingFiles
  ) => {
    const files = Array.from(
      incomingFiles || []
    );

    if (!files.length) return;

    setIsProcessing(true);

    try {
      const compressedFiles =
        await Promise.all(
          files.map(async (file) => {
            const options = {
              maxSizeMB: 0.3,
              maxWidthOrHeight: 1200,
              initialQuality: 0.7,
              useWebWorker: true,
              fileType: "image/webp",
            };

            return await imageCompression(
              file,
              options
            );
          })
        );

      /**
       * Append files in the same order they were received.
       */

      setImageFiles((prev) => [
        ...prev,
        ...compressedFiles,
      ]);

      /**
       * Build matching previews.
       */

      const newPreviews =
        compressedFiles.map((file) =>
          URL.createObjectURL(file)
        );

      setPreviews((prev) => [
        ...prev,
        ...newPreviews,
      ]);
    } catch (error) {
      console.error(
        "Image compression failed:",
        error
      );
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * ============================================================
   * NATIVE CAMERA
   * ============================================================
   *
   * capture="environment" asks the phone/browser to use the
   * rear-facing native camera experience.
   *
   * The OS/browser decides whether it returns after each photo.
   * On most phones, the user will take one photo, return here,
   * then tap "Take Another Photo".
   * ============================================================
   */

  const handleCameraChange = async (e) => {
    const files = Array.from(
      e.target.files || []
    );

    if (!files.length) return;

    await processAndAppendImages(files);

    /**
     * Reset so taking another photo works even if the browser
     * happens to produce the same filename.
     */

    e.target.value = "";
  };

  const triggerCamera = () => {
    if (!isProcessing) {
      cameraInputRef.current?.click();
    }
  };

  /**
   * ============================================================
   * FILE PICKER
   * ============================================================
   */

  const handleFileChange = async (e) => {
    const files = Array.from(
      e.target.files || []
    );

    if (!files.length) return;

    await processAndAppendImages(files);

    e.target.value = "";
  };

  const triggerFileInput = () => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  };

  /**
   * ============================================================
   * REMOVE IMAGE
   * ============================================================
   */

  const handleRemovePreview = (index) => {
    const url = previews[index];

    if (url?.startsWith?.("blob:")) {
      URL.revokeObjectURL(url);
    }

    setPreviews((prev) =>
      prev.filter((_, i) => i !== index)
    );

    setImageFiles((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  /**
   * ============================================================
   * REORDER IMAGE
   * ============================================================
   *
   * Both arrays must move together so the displayed preview
   * always stays paired with the correct File object.
   * ============================================================
   */

  const moveImage = (
    index,
    direction
  ) => {
    const newIndex =
      index + direction;

    if (
      newIndex < 0 ||
      newIndex >= previews.length
    ) {
      return;
    }

    setPreviews((prev) => {
      const next = [...prev];

      [
        next[index],
        next[newIndex],
      ] = [
        next[newIndex],
        next[index],
      ];

      return next;
    });

    setImageFiles((prev) => {
      const next = [...prev];

      [
        next[index],
        next[newIndex],
      ] = [
        next[newIndex],
        next[index],
      ];

      return next;
    });
  };

  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="hobbli-image-upload">
      <p>
        <strong>Images</strong>
      </p>

      <p
        style={{
          fontSize: "14px",
          color: "#666",
          margin: "0 0 10px 0",
        }}
      >
        Take photos with your device camera
        or choose existing images. The first
        image will be used as the primary
        image.
      </p>

      {/* ======================================================
          PHOTO ACTIONS
          ====================================================== */}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <button
          type="button"
          onClick={triggerCamera}
          disabled={isProcessing}
          style={{
            padding: "10px 20px",
            backgroundColor: isProcessing
              ? "#999"
              : "#007bff",
            color: "#fff",
            borderRadius: "5px",
            cursor: isProcessing
              ? "not-allowed"
              : "pointer",
            fontWeight: "bold",
            border: "none",
            fontSize: "16px",
          }}
        >
          {isProcessing
            ? "Processing..."
            : previews.length
            ? "📷 Take Another Photo"
            : "📷 Take Photo"}
        </button>

        <button
          type="button"
          onClick={triggerFileInput}
          disabled={isProcessing}
          style={{
            padding: "10px 20px",
            backgroundColor: isProcessing
              ? "#999"
              : "#6c757d",
            color: "#fff",
            borderRadius: "5px",
            cursor: isProcessing
              ? "not-allowed"
              : "pointer",
            fontWeight: "bold",
            border: "none",
            fontSize: "16px",
          }}
        >
          🖼 Choose Existing
        </button>
      </div>

      {/* ======================================================
          NATIVE CAMERA INPUT
          ====================================================== */}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraChange}
        style={{
          display: "none",
        }}
      />

      {/* ======================================================
          NORMAL FILE PICKER
          ====================================================== */}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{
          display: "none",
        }}
      />

      {/* ======================================================
          IMAGE PREVIEWS
          ====================================================== */}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        {previews.map(
          (src, idx) => (
            <div
              key={`${src}-${idx}`}
              style={{
                width: 100,
                position: "relative",
              }}
            >
              {/* IMAGE */}

              <div
                style={{
                  width: 100,
                  height: 100,
                  position: "relative",
                }}
              >
                <img
                  src={src}
                  alt={`Preview ${
                    idx + 1
                  }`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "5px",

                    border:
                      idx === 0
                        ? "2px solid #007bff"
                        : "1px solid rgba(128,128,128,0.4)",
                  }}
                />

                {/* ORDER / PRIMARY BADGE */}

                <div
                  style={{
                    position:
                      "absolute",
                    left: "4px",
                    bottom: "4px",
                    minWidth:
                      "20px",
                    height: "20px",
                    padding:
                      "0 5px",
                    borderRadius:
                      "10px",
                    backgroundColor:
                      idx === 0
                        ? "#007bff"
                        : "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    fontSize:
                      "11px",
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    fontWeight:
                      "bold",
                  }}
                >
                  {idx === 0
                    ? "Primary"
                    : idx + 1}
                </div>

                {/* REMOVE */}

                <button
                  type="button"
                  onClick={() =>
                    handleRemovePreview(
                      idx
                    )
                  }
                  aria-label={`Remove image ${
                    idx + 1
                  }`}
                  style={{
                    position:
                      "absolute",
                    top: "-8px",
                    right: "-8px",
                    width: "24px",
                    height: "24px",
                    borderRadius:
                      "50%",
                    backgroundColor:
                      "#dc3545",
                    color: "#fff",
                    border: "none",
                    cursor:
                      "pointer",
                    fontSize:
                      "16px",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                  }}
                >
                  ✕
                </button>
              </div>

              {/* ==================================================
                  REORDER CONTROLS
                  ================================================== */}

              {previews.length >
                1 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1fr 1fr",
                    gap: "4px",
                    marginTop:
                      "5px",
                  }}
                >
                  <button
                    type="button"
                    disabled={
                      idx === 0
                    }
                    onClick={() =>
                      moveImage(
                        idx,
                        -1
                      )
                    }
                    aria-label={`Move image ${
                      idx + 1
                    } left`}
                    style={{
                      height:
                        "28px",
                      border:
                        "1px solid #ccc",
                      borderRadius:
                        "4px",
                      background:
                        "#f5f5f5",
                      cursor:
                        idx === 0
                          ? "default"
                          : "pointer",
                      opacity:
                        idx === 0
                          ? 0.4
                          : 1,
                      fontSize:
                        "18px",
                    }}
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    disabled={
                      idx ===
                      previews.length -
                        1
                    }
                    onClick={() =>
                      moveImage(
                        idx,
                        1
                      )
                    }
                    aria-label={`Move image ${
                      idx + 1
                    } right`}
                    style={{
                      height:
                        "28px",
                      border:
                        "1px solid #ccc",
                      borderRadius:
                        "4px",
                      background:
                        "#f5f5f5",
                      cursor:
                        idx ===
                        previews.length -
                          1
                          ? "default"
                          : "pointer",
                      opacity:
                        idx ===
                        previews.length -
                          1
                          ? 0.4
                          : 1,
                      fontSize:
                        "18px",
                    }}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}